import React, { useMemo, useState } from 'react';
import { H } from './quantum/gates';
import { zeroState, basisState, applyGate, densityMatrixFromState, mixDensityMatrices, blochVector, qotpEncrypt, qotpDecrypt } from './quantum/QuantumState';
import BlochSphereCanvas from './BlochSphereCanvas.jsx';

const MESSAGE_STATES = {
  '0': { label: '|0⟩', state: zeroState(1) },
  '1': { label: '|1⟩', state: basisState(1, 1) },
  '+': { label: '|+⟩', state: applyGate(zeroState(1), H, 0, 1) },
};

const KEY_NAMES = { '00': 'I (do nothing)', '01': 'Z', '10': 'X', '11': 'XZ' };

// The attacker's view doesn't depend on which key was actually used — it's
// the average over all 4 equally-likely keys. This is computed once per
// original state and is always I/2, per the Stage-1-verified security proof.
const attackerView = (state) => {
  const keys = [[0, 0], [0, 1], [1, 0], [1, 1]];
  const weighted = keys.map(([a, b]) => ({ prob: 0.25, rho: densityMatrixFromState(qotpEncrypt(state, a, b)) }));
  return mixDensityMatrices(weighted);
};

function QOTPDemo({ msgId, setMsgId, title, presetLabels }) {
  const [key, setKey] = useState(null); // { a, b }
  const [showAttacker, setShowAttacker] = useState(false);
  const [decrypted, setDecrypted] = useState(false);

  const original = MESSAGE_STATES[msgId].state;
  const originalVector = useMemo(() => blochVector(densityMatrixFromState(original)), [original]);
  const attackerRho = useMemo(() => attackerView(original), [original]);
  const attackerVector = blochVector(attackerRho);

  const encrypted = key ? qotpEncrypt(original, key.a, key.b) : null;
  const encryptedVector = encrypted ? blochVector(densityMatrixFromState(encrypted)) : null;
  const recovered = key && decrypted ? qotpDecrypt(encrypted, key.a, key.b) : null;
  const recoveredVector = recovered ? blochVector(densityMatrixFromState(recovered)) : null;

  const generateKey = () => {
    const a = Math.random() < 0.5 ? 0 : 1;
    const b = Math.random() < 0.5 ? 0 : 1;
    setKey({ a, b });
    setShowAttacker(false);
    setDecrypted(false);
  };

  const reset = () => {
    setKey(null);
    setShowAttacker(false);
    setDecrypted(false);
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <h2 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">{title}</h2>
        <div className="flex flex-wrap gap-2">
          {presetLabels.map((id) => (
            <button
              key={id}
              onClick={() => { setMsgId(id); reset(); }}
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${msgId === id ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-50 text-gray-700 border border-gray-200 hover:border-blue-300'}`}
            >
              {MESSAGE_STATES[id].label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm text-center">
          <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Original State |ψ⟩</h3>
          <div className="flex justify-center"><BlochSphereCanvas vector={originalVector} size={160} showMixed={false} /></div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm text-center">
          <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Encrypted State</h3>
          {key ? (
            <>
              <div className="flex justify-center"><BlochSphereCanvas vector={encryptedVector} size={160} showMixed={false} /></div>
              <p className="text-xs text-gray-500 mt-2 font-mono">Key: {key.a}{key.b} → applied {KEY_NAMES[`${key.a}${key.b}`]}</p>
            </>
          ) : (
            <div className="h-[160px] flex items-center justify-center text-gray-400 text-sm italic">Generate a key first</div>
          )}
        </div>

        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm text-center">
          <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Attacker's View</h3>
          {showAttacker ? (
            <>
              <div className="flex justify-center"><BlochSphereCanvas vector={attackerVector} size={160} showMixed /></div>
              <p className="text-xs text-gray-500 mt-2">Secret key: <strong className="text-red-600">UNKNOWN</strong></p>
            </>
          ) : (
            <div className="h-[160px] flex items-center justify-center text-gray-400 text-sm italic">Not viewed yet</div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm flex flex-wrap justify-center gap-2">
        <button onClick={generateKey} className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-bold text-sm">
          🎲 Generate random key & encrypt
        </button>
        <button onClick={() => setShowAttacker(true)} disabled={!key} className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-bold text-sm disabled:opacity-40">
          🕵️ View as attacker (no key)
        </button>
        <button onClick={() => setDecrypted(true)} disabled={!key} className="px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-bold text-sm disabled:opacity-40">
          🔓 Decrypt (with key)
        </button>
        <button onClick={reset} className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-sm">
          ↻ Reset
        </button>
      </div>

      {decrypted && recovered && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
          <p className="text-green-800 font-semibold text-sm">
            ✓ Decrypted with the correct key — recovered Bloch vector ({recoveredVector.x.toFixed(2)}, {recoveredVector.y.toFixed(2)}, {recoveredVector.z.toFixed(2)})
            exactly matches the original ({originalVector.x.toFixed(2)}, {originalVector.y.toFixed(2)}, {originalVector.z.toFixed(2)}).
          </p>
        </div>
      )}
    </div>
  );
}

export default function SecurityLab() {
  const [tab, setTab] = useState('qotp');
  const [msgId, setMsgId] = useState('+');

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-4xl font-bold text-gray-900 mb-1">Quantum Security Lab</h1>
          <p className="text-gray-600">The Quantum One-Time Pad (QOTP): why an unknown random key leaves an eavesdropper with no information.</p>
        </div>

        <div className="flex justify-center gap-3 mb-6">
          {[
            { id: 'qotp', label: '🔑 QOTP' },
            { id: 'medical', label: '🏥 Medical Data Privacy (Educational)' },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${tab === t.id ? 'bg-purple-600 text-white shadow-md' : 'bg-white text-gray-600 border border-gray-200'}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'qotp' && (
          <QOTPDemo msgId={msgId} setMsgId={setMsgId} title="Choose a state to encrypt" presetLabels={['0', '1', '+']} />
        )}

        {tab === 'medical' && (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 text-center text-sm text-amber-800 font-semibold">
              ⚠️ Simplified Educational Example — this is not a real clinical encryption system.
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm text-sm text-gray-700 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              <div><div className="text-gray-400 text-xs">Patient ID</div><div className="font-mono font-bold">P001</div></div>
              <div><div className="text-gray-400 text-xs">Age</div><div className="font-mono font-bold">45</div></div>
              <div><div className="text-gray-400 text-xs">Disease</div><div className="font-mono font-bold">Diabetes</div></div>
              <div><div className="text-gray-400 text-xs">Value → encoded</div><div className="font-mono font-bold text-purple-700">YES → |1⟩</div></div>
            </div>
            <QOTPDemo msgId={'1'} setMsgId={() => {}} title="Encoded record (fixed to |1⟩ = 'YES' for this example)" presetLabels={['1']} />
            <p className="text-xs text-gray-500 text-center">
              The "Attacker's View" panel above is the same math as the QOTP tab: without the key, the encrypted record is indistinguishable from I/2 — an attacker
              learns nothing about whether the value was YES or NO.
            </p>
          </div>
        )}

        <div className="mt-6 bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">The math behind it</h3>
          <p className="font-mono text-sm text-gray-800 text-center mb-2">
            ρ<sub>enc</sub> = ¼(ρ + XρX + ZρZ + XZρZX) = I/2
          </p>
          <p className="text-sm text-gray-600">
            When the secret key is uniformly random and unknown, the attacker's averaged quantum state contains no information about the original one-qubit
            state — for <em>any</em> starting state, this always works out to the fully-mixed state I/2 (verified directly in this codebase's test suite for
            several different starting states, not just asserted).
          </p>
          <p className="text-sm text-gray-500 mt-2">
            <strong>Important:</strong> this means QOTP provides <em>information-theoretic security under the proper key-management and implementation
            assumptions</em> — a one-time key, used once, kept secret. It is not a claim that "quantum encryption can never be broken" in general; a reused key,
            a leaked key, or an implementation flaw would break it just like any cryptosystem.
          </p>
        </div>
      </div>
    </div>
  );
}
